'use client'

import { KeyboardEvent, ReactNode } from 'react'

export type LoanFillMode = 'import' | 'manual'

interface Props {
    mode: LoanFillMode
    onModeChange: (mode: LoanFillMode) => void
    cardsHidden?: boolean
    children?: ReactNode
}

const IMPORT_CHIPS = ['≈ 30 secondes', 'Champs remplis', "Moins d'erreurs"]

const Chip = ({ label, active }: { label: string; active: boolean }) => (
    <span
        className={`rounded-full border px-3 py-1 text-[12px] ${
            active ? 'border-rm-blue bg-white text-rm-blue-accent' : 'border-rm-gray-light text-pale/60'
        }`}
    >
        {label}
    </span>
)

const cardClass = (isSelected: boolean) =>
    `flex h-full cursor-pointer flex-col gap-2 p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue md:p-5 ${
        isSelected ? 'border-2 border-rm-blue bg-rm-blue-accent-bg' : 'border-2 border-rm-gray-light bg-white'
    }`

const Radio = ({ isSelected }: { isSelected: boolean }) => (
    <span
        aria-hidden="true"
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
            isSelected ? 'border-rm-blue' : 'border-rm-gray'
        }`}
    >
        {isSelected && <span className="size-2.5 rounded-full bg-rm-blue" />}
    </span>
)

export default function LoanFillModeSelector({ mode, onModeChange, cardsHidden = false, children }: Props) {
    const isImport = mode === 'import'

    const handleKeyDown = (next: LoanFillMode) => (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== ' ' && event.key !== 'Enter') return
        event.preventDefault()
        onModeChange(next)
    }

    return (
        <div className="mb-6">
            {!cardsHidden && (
                <h3 className="m-0 mb-6 font-recoleta text-[17px] font-bold text-pale md:text-[19px]">
                    Comment souhaitez-vous remplir cette étape ?
                </h3>
            )}

            <div
                role="radiogroup"
                aria-label="Mode de remplissage de l'étape"
                className="mt-3 flex flex-col gap-4 md:grid md:grid-cols-2"
            >
                <div
                    role="radio"
                    aria-checked={isImport}
                    tabIndex={0}
                    onClick={() => onModeChange('import')}
                    onKeyDown={handleKeyDown('import')}
                    data-test="fill-mode-import"
                    className={`relative order-1 rounded-2xl ${cardsHidden ? 'hidden' : ''} ${cardClass(isImport)} ${
                        isImport && children ? 'rounded-b-none border-b-0 md:rounded-b-2xl md:border-b-2' : ''
                    }`}
                >
                    <span className="absolute -top-3 left-4 rounded-full bg-rm-blue px-3 py-0.5 text-[11px] font-bold uppercase text-white">
                        Recommandé
                    </span>

                    <span className="flex items-start gap-3">
                        <Radio isSelected={isImport} />
                        <span
                            style={{ fontFamily: 'var(--font-recoleta)' }}
                            className="text-[17px] font-bold text-pale"
                        >
                            Importer mon offre de prêt
                        </span>
                    </span>

                    <span className="text-[14px] text-pale/80">
                        Nous lisons le document et remplissons les lignes de prêt, le différé et vos garanties. Vous
                        n&apos;avez plus qu&apos;à vérifier.
                    </span>

                    <span className="flex flex-wrap gap-2">
                        {IMPORT_CHIPS.map((chip) => (
                            <Chip key={chip} label={chip} active={isImport} />
                        ))}
                    </span>
                </div>

                {children && (
                    <div
                        role="group"
                        aria-label="Import de votre offre de prêt"
                        className={`order-2 md:order-3 md:col-span-2 ${
                            isImport && !cardsHidden
                                ? '-mt-4 rounded-b-2xl border-x-2 border-b-2 border-rm-blue bg-rm-blue-accent-bg p-3 pt-0 md:mt-0 md:rounded-none md:border-0 md:bg-transparent md:p-0'
                                : ''
                        }`}
                    >
                        {children}
                    </div>
                )}

                <div
                    role="radio"
                    aria-checked={!isImport}
                    tabIndex={0}
                    onClick={() => onModeChange('manual')}
                    onKeyDown={handleKeyDown('manual')}
                    data-test="fill-mode-manual"
                    className={`order-3 rounded-2xl md:order-2 ${cardsHidden ? 'hidden' : ''} ${cardClass(!isImport)}`}
                >
                    <span className="flex items-start gap-3">
                        <Radio isSelected={!isImport} />
                        <span
                            style={{ fontFamily: 'var(--font-recoleta)' }}
                            className="text-[17px] font-bold text-pale"
                        >
                            Saisir manuellement
                        </span>
                    </span>

                    <span className="text-[14px] text-pale/80">
                        {isImport
                            ? "Vous renseignez vous-même chaque ligne de prêt. Utile si vous n'avez pas encore reçu votre offre."
                            : 'Vous renseignez chaque ligne de prêt. Les champs sont dépliés ci-dessous.'}
                    </span>

                    <span className="flex flex-wrap gap-2">
                        <Chip label="≈ 3 minutes" active={!isImport} />
                    </span>
                </div>
            </div>
        </div>
    )
}
